<script setup lang="ts">
import LGFooter from "~/components/footer/LGFooter.vue";
import LGHeader from "~/components/header/LGHeader.vue";
import type { NavigationMenuItem } from "@nuxt/ui";
import { useAuthStore } from "~~/stores/auth";

const { t } = useI18n();
const localePath = useLocalePath();
const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

const menuClasses =
    "!text-[#402B19] uppercase transition justify-center md:justify-start !text-[20px] lg:!text-[20px] !px-0 hover:!text-primary cursor-pointer";

// --- helpers: normalize and match current/section paths (locale-aware) ---
const normalize = (p: string) => (p ? p.replace(/\/+$/, "") : "/");
const isCurrent = (target: string) =>
    normalize(route.path) === normalize(target);
const isSection = (target: string) => {
    const cur = normalize(route.path);
    const base = normalize(target);
    return cur === base || cur.startsWith(base + "/");
};

// --- resolve localized paths ONCE and reuse ---
const paths = computed(() => ({
    profile: localePath("moj-racun"),
    library: localePath("moj-racun-moji-vodici"),
    orders: localePath("moj-racun-moji-nakupi"),
    login: localePath("moj-racun-prijava"),
}));

function getClasses(active: boolean) {
    return [menuClasses, active ? "!text-primary font-bold" : ""].join(" ");
}

// --- background + pin depend on section, use locale-aware startsWith ---
const panelBg = computed<string>(() => {
    if (isSection(paths.value.library)) return "before:bg-[#EEEEFA]";
    if (isSection(paths.value.orders)) return "before:bg-[#EDF7FA]";
    return "before:bg-[#FAEFEE]";
});

const pinImage = computed<string>(() => {
    if (isSection(paths.value.library))
        return "/images/layouts/user-pin-purple.svg";
    if (isSection(paths.value.orders))
        return "/images/layouts/user-pin-blue.svg";
    return "/images/layouts/user-pin-orange.svg";
});

const userMenuItems = computed<NavigationMenuItem[][]>(() => {
    const items: NavigationMenuItem[] = [
        {
            label: t("userProfile.sidebar.profile"),
            to: paths.value.profile,
            class: getClasses(isCurrent(paths.value.profile)),
            active: isCurrent(paths.value.profile),
        },
        {
            label: t("userProfile.sidebar.myGuides"),
            to: paths.value.library,
            class: getClasses(isSection(paths.value.library)),
            active: isSection(paths.value.library),
        },
        {
            label: t("userProfile.sidebar.myOrders"),
            to: paths.value.orders,
            class: getClasses(isSection(paths.value.orders)),
            active: isSection(paths.value.orders),
        },
    ];

    if (
        auth.user &&
        Array.isArray(auth.user.role) &&
        auth.user.role.includes("admin")
    ) {
        items.push({
            label: "Administracija",
            to: "/administracija",
            class: getClasses(isSection("/administracija")),
            active: isSection("/administracija"),
        });
    }

    items.push({
        label: t("userProfile.sidebar.logout"),
        class: getClasses(false),
        onSelect: logout,
    });

    return [items];
});

async function logout() {
    await auth.logout();
    await router.push(paths.value.login);
}
</script>

<template>
    <div>
        <LGHeader/>
        <div class="relative overflow-x-hidden" role="main">
            <NuxtImg :src="pinImage" alt="Title" class="hidden md:block absolute left-0 top-12 w-full h-auto max-w-[212px] lg:max-w-[317px]" />
            <UContainer>
                <div class="min-h-[100vh] grid grid-cols-12">
                    <div class="col-span-12 md:col-span-5 pt-16 pb-12 lg:pt-60 lg:pb-72 flex flex-col justify-start items-center md:items-start gap-12 relative h-full">
                        <div class="w-full max-w-[350px] flex flex-col items-center md:items-start md:justify-start gap-4 sticky top-24">
                            <NuxtImg :src="pinImage" alt="Title" class="md:hidden -ml-4 w-full h-auto max-w-[212px] lg:max-w-[317px] mr-auto" />
                            <h1 class="uppercase font-bold text-center md:text-start text-[30px] leading-[38px] lg:text-[46px] lg:leading-[48px]">
                                {{ t('userProfile.sidebar.profile') }}
                            </h1>
                            <USeparator v-if="auth.user != null" type="solid" class="user-menu-separator" />
                            <UNavigationMenu v-if="auth.user != null" orientation="vertical" variant="link" :items="userMenuItems" class="w-full" />
                        </div>
                    </div>
                    <div class="col-span-12 md:col-span-7 relative pt-12 pb-60 lg:pt-36 lg:pb-72 md:pl-24 before:absolute before:top-0 before:-left-4 before:md:left-0 before:right-0 before:w-[200%] before:h-full before:z-0" :class="panelBg">
                        <NuxtPage />
                    </div>
                </div>
            </UContainer>
        </div>
        <LGFooter/>
    </div>
</template>

<style>
.user-menu-separator .border-default {
    border-top-width: 3px;
    border-top-color: #EFEFEF;
}
</style>
